import { Box, Stack, SxProps, Typography } from '@mui/material';
import * as React from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Switch } from '@/components/form/baseInputs/Switch';
import { Copy02Icon } from '@/components/icons/Copy02Icon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { PresentationChart03Icon } from '@/components/icons/PresentationChart03Icon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { SlideEditorRichTextEditor } from '@/modules/slideEditor/textEditors/SlideEditor.richTextEditor';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';

export interface EntityNotesPerDeathEditorProps {
  notesForDeath: string;
  isGenerating?: boolean;
  openEditor: () => void;
  onAddToPresentation?: () => void;
}

export function EntityNotesPerDeathEditor({
  notesForDeath,
  openEditor,
}: EntityNotesPerDeathEditorProps) {
  const [isHovered, elementProps] = useIsElementHovered();

  if (!notesForDeath) {
    return (
      <EditOverlay
        openEditor={openEditor}
        message="Add notes in live preview"
      />
    );
  }

  return (
    <Box {...elementProps}>
      <Box sx={{ opacity: isHovered ? 0.2 : 1 }}>
        <SlideEditorRichTextEditor content={notesForDeath} />
      </Box>
      {isHovered && (
        <EditOverlay
          openEditor={openEditor}
          message="Edit notes in live preview"
        />
      )}
    </Box>
  );
}

export function EntityNotesPerDeathEditorV2({
  notesForDeath,
  isGenerating,
  onAddToPresentation,
}: EntityNotesPerDeathEditorProps) {
  const [isHovered, elementProps] = useIsElementHovered();
  const { createSuccessFeedback, createErrorFeedback } = useFeedback();
  const canEdit = useCanEditHouseholdData();
  const [copyToClipboard] = useCopyToClipboard();

  if (isGenerating) {
    return (
      <Stack sx={CONTAINER_OVERLAY_SX}>
        <Stack alignItems="center" justifyContent="center">
          <Loader circularProgressProps={{ size: 20 }} />
          <Typography variant="title-s">Generating notes</Typography>
          <Typography variant="body-xs">
            This may take up to 10 seconds
          </Typography>
        </Stack>
      </Stack>
    );
  }

  if (!notesForDeath) {
    return (
      <Stack sx={CONTAINER_OVERLAY_SX}>
        <Typography variant="body-xs">No client presentation notes</Typography>
      </Stack>
    );
  }

  return (
    <Box
      {...elementProps}
      justifyContent="center"
      data-testid="entity-notes-per-death-editor"
    >
      <Box sx={{ opacity: isHovered ? 0.2 : 1 }}>
        <Box>
          <SlideEditorRichTextEditor content={notesForDeath} />
        </Box>{' '}
      </Box>
      {isHovered && (
        <Box sx={CONTAINER_OVERLAY_SX} width="100%">
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={Copy02Icon}
              onClick={() => {
                copyToClipboard(
                  notesForDeath,
                  createSuccessFeedback('Copied to clipboard'),
                  createErrorFeedback('Failed to copy to clipboard')
                );
              }}
              size="sm"
              variant="secondary"
            >
              Copy to clipboard
            </Button>
            {canEdit && onAddToPresentation && (
              <Button
                startIcon={PresentationChart03Icon}
                onClick={onAddToPresentation}
                size="sm"
                variant="secondary"
              >
                Add to presentation
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

const CONTAINER_OVERLAY_SX: SxProps = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(1, 1, 1, 0.02)',
};

interface EditOverlayProps
  extends Pick<EntityNotesPerDeathEditorProps, 'openEditor'> {
  message: string;
}

function EditOverlay({ openEditor, message }: EditOverlayProps) {
  return (
    <Box sx={CONTAINER_OVERLAY_SX}>
      <Button variant="secondary" size="sm" onClick={openEditor}>
        {message}
      </Button>
    </Box>
  );
}

interface EntityNotesPerDeathEditorFooterProps {
  openModal: () => void;
  openGenerateAiNotesModal: () => void;
  isGenerating?: boolean;
  updatedAt?: Date;
  specifyNotesPerClient: boolean;
  handleSwitchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  generatedCredit: string | undefined;
  addNotesCopy: string;
  showToggle: boolean;
}

export function EntityNotesPerDeathEditorFooter({
  openModal,
  openGenerateAiNotesModal,
  isGenerating,
  specifyNotesPerClient,
  handleSwitchChange,
  generatedCredit,
  addNotesCopy,
  showToggle,
}: EntityNotesPerDeathEditorFooterProps) {
  const aiCapabilitiesEnabled = useAICapabilitiesEnabled();
  const canEdit = useCanEditHouseholdData();
  return (
    <Stack
      direction="row"
      gap={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row" gap={1} alignItems="center">
        {canEdit && (
          <Button
            size="sm"
            variant="secondary"
            startIcon={Edit02Icon}
            onClick={openModal}
          >
            {addNotesCopy}
          </Button>
        )}
        {canEdit && aiCapabilitiesEnabled && (
          <Button
            size="sm"
            variant="secondary"
            startIcon={Stars01Icon}
            onClick={openGenerateAiNotesModal}
            disabled={isGenerating}
          >
            Generate with AI
          </Button>
        )}
        {canEdit && showToggle && (
          <Switch
            label="Notes per client"
            labelPosition="right"
            onChange={handleSwitchChange}
            value={specifyNotesPerClient}
          />
        )}
      </Stack>
      <Typography variant="body-xs">{generatedCredit}</Typography>
    </Stack>
  );
}
